// Overlay iframe URLs and the prefetch-cache keys they map to.
import { shownAsType } from "@/modules/ExperienceAttributes";
import type ExperienceAttributes from "@/modules/ExperienceAttributes";
import {
  OVERLAY_OPEN_MIN_STAGE,
  isMediaWarmStageReady,
} from "@/modules/experiencePrefetchWarm";
import { experienceIdFromPublisherPayload } from "@/popover/anchorPrefetch";
import type { InflightRegistry, PrefetchBucket } from "@/popover/types";

/**
 * "Active" means either a completed prefetch entry exists, OR a fetch is
 * currently in flight. In-flight counts so the iframe URL carries
 * `pix-embed-prefetch=1` and the child waits for parent postMessage
 * (delivered by _notifyOpenOverlayOfPrefetchResolution on resolve)
 * instead of falling back to its own API request after its short timeout.
 */
export function prefetchEntryReadyForAttrs(
  attrs: ExperienceAttributes,
): boolean {
  const bucket: PrefetchBucket =
    (window.__pixmoto || {}).prefetchedExperiences || {};
  const inflight: InflightRegistry =
    (window.__pixmoto || {})._prefetchInflight || {};
  const expId = attrs.getExperienceId();
  if (expId) {
    const key = String(expId);
    if (inflight[key]) {
      return true;
    }
    const byId = bucket[key];
    return !!(byId && byId.status === "ok" && byId.data !== undefined);
  }
  const u = attrs.getUrl();
  if (u) {
    const urlKey = `url:${u}`;
    if (inflight[urlKey]) {
      return true;
    }
    const byUrl = bucket[urlKey];
    return !!(byUrl && byUrl.status === "ok" && byUrl.data !== undefined);
  }
  return false;
}

export function resolvePrefetchExperienceId(
  attrs: ExperienceAttributes,
): string | null {
  const expId = attrs.getExperienceId();
  if (expId) {
    return String(expId);
  }
  const bucket: PrefetchBucket =
    (window.__pixmoto || {}).prefetchedExperiences || {};
  const u = attrs.getUrl();
  if (!u) {
    return null;
  }
  const entry = bucket[`url:${u}`];
  if (!entry || entry.status !== "ok" || entry.data === undefined) {
    return null;
  }
  return experienceIdFromPublisherPayload(entry.data);
}

export function buildOverlayPlayUrl(
  attrs: ExperienceAttributes,
  isAuto: boolean,
  cacheKey: string | null,
): string {
  let url = attrs.getPlayUrl(isAuto, shownAsType.OVERLAY);
  const resolvedPrefetchId = resolvePrefetchExperienceId(attrs);

  if (resolvedPrefetchId && !attrs.getExperienceId()) {
    url +=
      (url.indexOf("?") >= 0 ? "&" : "?") +
      "experience_id=" +
      encodeURIComponent(resolvedPrefetchId);
  }

  if (resolvedPrefetchId && prefetchEntryReadyForAttrs(attrs)) {
    url += `${url.indexOf("?") >= 0 ? "&" : "?"}pix-embed-prefetch=1`;
  }

  if (cacheKey && isMediaWarmStageReady(cacheKey, OVERLAY_OPEN_MIN_STAGE)) {
    url += `${url.indexOf("?") >= 0 ? "&" : "?"}pix-media-warm=1`;
  }
  url += `${url.indexOf("?") >= 0 ? "&" : "?"}pix-embed-shell=1`;
  return url;
}

export function overlayExperienceKey(url: string): string {
  try {
    const u = new URL(url, window.location.href);
    const q =
      u.searchParams.get("pix-expid") ||
      u.searchParams.get("expid") ||
      u.searchParams.get("experience_id");
    if (q) {
      return String(q);
    }
    const pathParts = u.pathname.replace(/\/+$/, "").split("/");
    const pathId = Number.parseInt(pathParts[pathParts.length - 1], 10);
    if (!Number.isNaN(pathId) && pathId > 0) {
      return String(pathId);
    }
  } catch (_e) {
    /* ignore */
  }
  return normalizeOverlayUrl(url);
}

export function normalizeOverlayUrl(url: string): string {
  if (!url) {
    return "";
  }
  try {
    return new URL(url, window.location.href).href;
  } catch (_e) {
    return String(url);
  }
}

export function cacheKeyFromAttrs(attrs: ExperienceAttributes): string | null {
  const expId = attrs.getExperienceId();
  if (expId) {
    return String(expId);
  }
  const mapPageUrl = attrs.getUrl();
  if (mapPageUrl) {
    return `url:${mapPageUrl}`;
  }
  return null;
}
